import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface AIProviderConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
}

export const AIProviderConfig = ({ data, onChange }: AIProviderConfigProps) => {
    // Default to 'default' which uses the tenant's integration keys
    const aiProvider = data.aiProvider || 'default';

    return (
        <div className="pt-4 border-t border-gray-100">
            <Label className="mb-2 block text-sm font-semibold text-gray-700 uppercase">AI Provider Settings</Label>

            <div className="mb-3">
                <Label className="text-xs font-normal text-gray-600">Provider</Label>
                <Select
                    value={aiProvider}
                    onValueChange={(value) => onChange('aiProvider', value)}
                >
                    <SelectTrigger className="w-full bg-white border-gray-300 shadow-sm text-sm h-8">
                        <SelectValue placeholder="Select AI provider" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-300 shadow-lg z-[100]">
                        <SelectItem value="default">Default Tenant Integration</SelectItem>
                        <SelectItem value="openai">OpenAI (Custom Key)</SelectItem>
                        <SelectItem value="azure">Azure OpenAI (Custom)</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {aiProvider === 'openai' && (
                <div className="mb-3 space-y-1 animate-in fade-in zoom-in-95 duration-200">
                    <Label className="text-xs font-normal text-gray-600">OpenAI API Key</Label>
                    <Input
                        type="password"
                        className="h-8 text-sm"
                        value={data.apiKey || ''}
                        onChange={(e) => onChange('apiKey', e.target.value)}
                        placeholder="sk-..."
                    />
                    <p className="text-[10px] text-gray-500">
                        Optional if OPENAI_API_KEY env var is set globally.
                    </p>
                </div>
            )}

            {aiProvider === 'azure' && (
                <div className="space-y-3 mb-3 animate-in fade-in zoom-in-95 duration-200">
                    <div className="space-y-1">
                        <Label className="text-xs font-normal text-gray-600">Azure API Key</Label>
                        <Input
                            type="password"
                            className="h-8 text-sm"
                            value={data.apiKey || ''}
                            onChange={(e) => onChange('apiKey', e.target.value)}
                            placeholder="Azure API Key"
                        />
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs font-normal text-gray-600">Azure Endpoint</Label>
                        <Input
                            className="h-8 text-sm"
                            value={data.azureEndpoint || ''}
                            onChange={(e) => onChange('azureEndpoint', e.target.value)}
                            placeholder="https://your-resource.openai.azure.com/"
                        />
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs font-normal text-gray-600">Deployment Name</Label>
                        <Input
                            className="h-8 text-sm"
                            value={data.azureDeployment || ''}
                            onChange={(e) => onChange('azureDeployment', e.target.value)}
                            placeholder="your-deployment-name"
                        />
                    </div>
                </div>
            )}

            {/* Common Model Tuning Options */}
            <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                    <Label className="text-xs font-normal text-gray-600">Model Name</Label>
                    <Input
                        className="h-8 text-sm"
                        value={data.modelName || 'gpt-4o-mini'}
                        onChange={(e) => onChange('modelName', e.target.value)}
                        placeholder="gpt-4o-mini"
                    />
                </div>

                <div className="space-y-1">
                    <Label className="text-xs font-normal text-gray-600">Max Tokens</Label>
                    <Input
                        type="number"
                        className="h-8 text-sm"
                        value={data.maxTokens || 1000}
                        onChange={(e) => onChange('maxTokens', parseInt(e.target.value))}
                        placeholder="1000"
                    />
                </div>
            </div>

            <div className="mt-3 space-y-1">
                <div className="flex justify-between items-center">
                    <Label className="text-xs font-normal text-gray-600">Temperature: {data.temperature || 0.2}</Label>
                </div>
                <Input
                    type="number"
                    className="h-8 text-sm"
                    step="0.1"
                    min="0"
                    max="2"
                    value={data.temperature || 0.2}
                    onChange={(e) => onChange('temperature', parseFloat(e.target.value))}
                    placeholder="0.2"
                />
            </div>
        </div>
    );
};
